import { HorizontalCardGroup, Icon, ScrollableList } from '@ps/isp-web-component-library';
import { Flame } from '@ps/isp-web-component-library/icons/Flame';
import { Translation } from '@ps/web-shared-components';
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { ExtractDispatchFromModel, ExtractSelectFromModel, RootState } from '@swf/core/store';
import { SportsComponent } from '@swf/sports/components/SportsComponent/SportsComponent';
import type { EventTypeId } from '@swf/sports/types/eventType';
import { ariaLabelScrollLeft, ariaLabelScrollRight } from '@swf/sports/utils/getAriaLabels';
import { getEventTypeIcon } from '@swf/sports/utils/getEventTypeIcon';

import type { SportsHomePageWidgetModel } from '../../../../widgets/sports-home-page-widget/model';
import type { SportsLandingPageWidgetModel } from '../../../../widgets/sports-landing-page-widget/model';

import { TXTWCL_Popular_Accumulators } from './PopularAccumulators.messages.json';
import styles from './PopularAccumulators.scss';
import PopularAccumulatorCard from './components/PopularAccumulatorCard/PopularAccumulatorCard';
import bgImage from './widget-bg.jpg';

interface Props {
  widgetSelect: Partial<
    | Pick<
        ExtractSelectFromModel<SportsHomePageWidgetModel>,
        | 'hasPopularAccumulators'
        | 'popularAccumulatorEventTypes'
        | 'selectedPopularAccumulatorsEventType'
        | 'popularAccumulators'
        | 'filteredPopularAccumulators'
      >
    | Pick<
        ExtractSelectFromModel<SportsLandingPageWidgetModel>,
        'popularAccumulators' | 'hasPopularAccumulators'
      >
  >;
  widgetDispatch?: Partial<
    Pick<
      ExtractDispatchFromModel<SportsHomePageWidgetModel>,
      'setSelectedPopularAccumulatorsEventType'
    >
  >;
}

// TODO: Sports to expose this from @ps/isp-web-component-library
export type HorizontalCardGroupRef = {
  reset: () => void;
  setActiveCard: () => void;
} | null;

export const PopularAccumulators: React.FC<Props> = ({ widgetSelect, widgetDispatch }) => {
  const hasPopularAccumulators = useSelector((state: RootState) =>
    widgetSelect.hasPopularAccumulators?.(state)
  );

  const tabs = useSelector((state: RootState) =>
    'popularAccumulatorEventTypes' in widgetSelect
      ? widgetSelect.popularAccumulatorEventTypes?.(state)
      : false
  );

  const selectedEventType = useSelector((state: RootState) =>
    'selectedPopularAccumulatorsEventType' in widgetSelect
      ? widgetSelect.selectedPopularAccumulatorsEventType?.(state)
      : false
  );

  const setSelectedEventType = useCallback(
    (eventTypeId: EventTypeId) => {
      widgetDispatch?.setSelectedPopularAccumulatorsEventType?.(eventTypeId);
    },
    [widgetDispatch]
  );

  const popularAccumulators = useSelector((state: RootState) =>
    'filteredPopularAccumulators' in widgetSelect
      ? widgetSelect.filteredPopularAccumulators?.(state)
      : widgetSelect.popularAccumulators?.(state)
  );

  const { formatMessage } = useRegulatoryIntl();
  const horizontalCardGroup = useRef<HorizontalCardGroupRef>(null);

  if (!hasPopularAccumulators || !popularAccumulators) return null;

  return (
    <SportsComponent name="Sports Popular Accumulators Component">
      <div className={styles.panel} style={{ backgroundImage: `url(${bgImage})` }}>
        <header className={styles.panelHeader}>
          <Icon size="xl" icon={Flame} label={`${formatMessage(TXTWCL_Popular_Accumulators)}`} />
          <span>
            <Translation {...TXTWCL_Popular_Accumulators} />
          </span>
        </header>
        {tabs && tabs.length > 1 ? (
          <ScrollableList
            space="xs"
            variant="square-scroll-arrows"
            background="translucent"
            slotProps={{
              previousButton: {
                'aria-label': formatMessage(ariaLabelScrollLeft)
              },
              nextButton: {
                'aria-label': formatMessage(ariaLabelScrollRight)
              }
            }}
          >
            {tabs.map((tab, index) => (
              <ScrollableList.Pill
                key={`eventType_${tab.eventTypeId}`}
                active={selectedEventType ? tab.eventTypeId === selectedEventType : index === 0}
                icon={getEventTypeIcon(tab.eventTypeId)}
                onClick={() => {
                  setSelectedEventType(tab.eventTypeId);
                  horizontalCardGroup.current?.reset();
                }}
              >
                {tab.name}
              </ScrollableList.Pill>
            ))}
          </ScrollableList>
        ) : null}
        <HorizontalCardGroup ref={horizontalCardGroup}>
          {popularAccumulators.map((popularAccumulator) => (
            <PopularAccumulatorCard
              key={popularAccumulator.runnerIds
                .map(({ marketId, selectionId }) => marketId + selectionId)
                .join('-')}
              popularAccumulator={popularAccumulator}
            />
          ))}
        </HorizontalCardGroup>
      </div>
    </SportsComponent>
  );
};
